import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Avatar,
    Paper,
    CircularProgress,
    Alert,
    Menu,
    MenuItem,
    Chip,
    Divider,
    IconButton
} from '@mui/material';
import {
    Send as SendIcon,
    AutoAwesome as BotIcon,
    Phone as PhoneIcon,
    Person as PersonIcon,
    Refresh as RefreshIcon,
    Message as MessageIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import axios from 'axios';

// AI Message Templates
const AI_MESSAGE_TEMPLATES = {
    dropoff: {
        title: "Drop-off Follow-up",
        description: "Customer visited but didn't complete action",
        generatePrompt: (customerData) => `Generate a friendly follow-up message for ${customerData.name} who visited our window tinting website and performed a price estimate but didn't complete the booking. Ask if we can assist them further. Keep it conversational and helpful.`
    },
    estimate_follow_up: {
        title: "Estimate Follow-up",
        description: "Following up on price estimate request",
        generatePrompt: (customerData) => `Generate a friendly follow-up message for ${customerData.name} who requested a price estimate for window tinting. Ask if they have any questions about the estimate or if they'd like to proceed with booking. Mention our expertise and quality service.`
    },
    booking_reminder: {
        title: "Booking Reminder",
        description: "Remind customer about their booking",
        generatePrompt: (customerData) => `Generate a friendly reminder message for ${customerData.name} about their upcoming window tinting appointment. Ask them to confirm and provide any last-minute instructions or questions.`
    },
    general_follow_up: {
        title: "General Follow-up",
        description: "General customer follow-up",
        generatePrompt: (customerData) => `Generate a friendly follow-up message for ${customerData.name} who showed interest in our window tinting services. Ask how we can help them with their automotive or residential window tinting needs.`
    }
};

const LeadConversationView = ({ lead, onClose }) => {
    const [conversation, setConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [aiMenuAnchor, setAiMenuAnchor] = useState(null);
    const [generatingAi, setGeneratingAi] = useState(false);
    const [height, setHeight] = useState(700); // Default height
    const [isResizing, setIsResizing] = useState(false);
    const [resizeDirection, setResizeDirection] = useState(null); // Only 'vertical' now
    const messagesEndRef = useRef(null);
    const resizeRefBottom = useRef(null);

    // Convert mobile number from 04XXXXXXXX to +614XXXXXXXX
    const formatPhoneNumber = (mobile) => {
        if (!mobile) return null;

        // Remove any spaces, dashes, or other formatting
        const cleaned = mobile.replace(/[^\d]/g, '');

        // Convert 04XXXXXXXX to +614XXXXXXXX
        if (cleaned.startsWith('04') && cleaned.length === 10) {
            return `+614${cleaned.substring(2)}`;
        }

        // If already in international format
        if (cleaned.startsWith('614') && cleaned.length === 12) {
            return `+${cleaned}`;
        }

        // Return as is if can't format
        return mobile;
    };

    const formattedNumber = formatPhoneNumber(lead?.mobileNumber || lead?.phone);
    const customerName = `${lead?.Customer_name || ''} ${lead?.Customer_surname || ''}`.trim() || 'Customer';

    // Scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Load or create conversation
    useEffect(() => {
        if (formattedNumber) {
            loadConversation();
        }
    }, [formattedNumber]);

    // Handle resize functionality
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizing) return;

            // Only handle vertical resizing now since width is auto-managed
            if (resizeDirection === 'vertical' || resizeDirection === 'both') {
                const rect = resizeRefBottom.current?.parentElement?.getBoundingClientRect();
                if (rect) {
                    const newHeight = e.clientY - rect.top;
                    setHeight(Math.max(400, Math.min(window.innerHeight * 0.9, newHeight))); // Min 400px, Max 90vh
                }
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            setResizeDirection(null);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = 'none';

            // Set cursor for vertical resize only
            if (resizeDirection === 'vertical') {
                document.body.style.cursor = 'ns-resize';
            }
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, resizeDirection]);

    const handleResizeStart = (direction) => (e) => {
        e.preventDefault();
        setIsResizing(true);
        setResizeDirection(direction);
    };

    const loadConversation = async () => {
        if (!formattedNumber) {
            setError('Invalid mobile number format');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // First, try to find existing conversation
            const response = await axios.get(`/api/twilio/conversations/by-phone/${encodeURIComponent(formattedNumber)}`);

            if (response.data) {
                setConversation(response.data);
                await loadMessages(response.data.sid);
            } else {
                // Create new conversation if none exists
                await createNewConversation();
            }
        } catch (error) {
            console.error('Error loading conversation:', error);

            if (error.response?.status === 404) {
                // Conversation doesn't exist, create new one
                await createNewConversation();
            } else {
                setError('Failed to load conversation');
            }
        } finally {
            setLoading(false);
        }
    };

    const createNewConversation = async () => {
        try {
            const response = await axios.post('/api/twilio/conversations', {
                friendlyName: `${customerName} (${formattedNumber})`,
                phoneNumber: formattedNumber
            });

            setConversation(response.data);
            setMessages([]); // New conversation has no messages
        } catch (error) {
            console.error('Error creating conversation:', error);
            setError('Failed to create conversation');
        }
    };

    const loadMessages = async (conversationSid) => {
        try {
            const response = await axios.get(`/api/twilio/conversations/${conversationSid}/messages`);
            setMessages(response.data);
        } catch (error) {
            console.error('Error loading messages:', error);
            setError('Failed to load messages');
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() || !conversation || sending) return;

        setSending(true);
        try {
            const response = await axios.post(`/api/twilio/conversations/${conversation.sid}/messages`, {
                body: newMessage.trim()
            });

            // Add the new message to the list
            setMessages(prev => [...prev, response.data]);
            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
            setError('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const generateAiMessage = async (template) => {
        setGeneratingAi(true);
        setAiMenuAnchor(null);

        try {
            const customerData = {
                name: customerName,
                mobile: formattedNumber,
                requestType: lead?.requestType,
                address: [lead?.cust_address, lead?.suburb, lead?.pcode].filter(Boolean).join(', '),
                dateCreated: lead?.createdAt
            };

            const response = await axios.post('/api/twilio/generate-ai-message', {
                template: template,
                customerData: customerData,
                prompt: AI_MESSAGE_TEMPLATES[template].generatePrompt(customerData)
            });

            setNewMessage(response.data.message);
        } catch (error) {
            console.error('Error generating AI message:', error);
            setError('Failed to generate AI message');
        } finally {
            setGeneratingAi(false);
        }
    };

    const refreshMessages = async () => {
        if (conversation) {
            await loadMessages(conversation.sid);
        }
    };

    const formatMessageTime = (dateString) => {
        try {
            return format(new Date(dateString), 'MMM d, h:mm a');
        } catch {
            return 'Unknown time';
        }
    };

    const isOutbound = (message) => {
        return message.author === 'system' || !message.author?.startsWith('+');
    };

    if (loading) {
        return (
            <Box sx={{
                width: '100%', // Use full available width
                minWidth: 400,
                maxWidth: '100%', // Allow full expansion within container
                height: height,
                minHeight: 400,
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#1a1a1a',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                border: '1px solid #333',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {/* Vertical Resize Handle Only */}
                <Box
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: '4px',
                        cursor: 'ns-resize',
                        bgcolor: 'transparent',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                    onMouseDown={handleResizeStart('vertical')}
                />
                <CircularProgress />
            </Box>
        );
    }

    // Calculate dynamic height based on message count
    // Always ensure minimum height matches customer information section
    const calculateHeight = () => {
        const baseHeight = '700px'; // Minimum height to match customer info section

        if (messages.length === 0) {
            return baseHeight; // Use base height for no messages
        } else if (messages.length <= 3) {
            return baseHeight; // Keep base height for few messages
        } else if (messages.length <= 8) {
            return '800px'; // Slightly taller for more messages
        } else if (messages.length <= 15) {
            return '85vh'; // Larger height for many messages
        } else {
            return '90vh'; // Maximum height for very many messages
        }
    };

    return (
        <Box sx={{
            width: '100%', // Use full available width
            minWidth: 400,
            maxWidth: '100%', // Allow full expansion within container
            height: height,
            minHeight: 400,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#1a1a1a',
            borderRadius: '12px', // Fully rounded for standalone appearance
            overflow: 'hidden', // Ensure content respects rounded corners
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)', // Add shadow for depth
            border: '1px solid #333',
            position: 'relative' // For resize handle positioning
        }}>
            {/* Vertical Resize Handle Only */}
            <Box
                ref={resizeRefBottom}
                sx={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: '4px',
                    cursor: 'ns-resize',
                    bgcolor: 'transparent',
                    zIndex: 10,
                    '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                    },
                    '&:active': {
                        bgcolor: 'rgba(255, 255, 255, 0.2)'
                    }
                }}
                onMouseDown={handleResizeStart('vertical')}
            />

            {/* Header */}
            <Box sx={{
                p: 2,
                borderBottom: '1px solid #333',
                bgcolor: '#2a2a2a',
                borderRadius: '12px 12px 0 0' // Rounded top corners
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6" sx={{ color: '#ffffff', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MessageIcon size={20} />
                        Messages
                    </Typography>
                    <IconButton onClick={refreshMessages} size="small" sx={{ color: '#9e9e9e' }}>
                        <RefreshIcon />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#34C759' }}>
                        <PersonIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 'bold' }}>
                            {customerName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
                            {formattedNumber}
                        </Typography>
                    </Box>
                </Box>

                {conversation && (
                    <Chip
                        label={`Conversation: ${conversation.friendlyName}`}
                        size="small"
                        sx={{
                            mt: 1,
                            bgcolor: '#333',
                            color: '#9e9e9e',
                            borderRadius: '16px' // More rounded chip
                        }}
                    />
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ m: 1 }}>
                    {error}
                </Alert>
            )}

            {/* Messages */}
            <Box sx={{
                flex: 1,
                overflow: 'auto',
                bgcolor: '#000000', // Pure black background like Apple Messages
                backgroundImage: `
                    radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.02) 0%, transparent 50%),
                    radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.02) 0%, transparent 50%)
                `,
                m: 0.5, // Small margin for rounded effect
                borderRadius: '8px' // Rounded for the middle section
            }}>
                {messages.length === 0 ? (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        flexDirection: 'column',
                        gap: 1
                    }}>
                        <MessageIcon sx={{ fontSize: 48, color: '#666' }} />
                        <Typography variant="body2" sx={{ color: '#8e8e93', textAlign: 'center' }}>
                            No messages yet
                            <br />
                            Start a conversation with {customerName}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ p: 1, pb: 2 }}>
                        {messages.map((message, index) => {
                            const isOwnMessage = isOutbound(message);
                            const prevMessage = index > 0 ? messages[index - 1] : null;
                            const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;
                            const showTime = !nextMessage ||
                                isOutbound(nextMessage) !== isOwnMessage ||
                                (new Date(message.dateCreated).getTime() - new Date(nextMessage.dateCreated).getTime()) > 300000; // 5 minutes

                            return (
                                <Box
                                    key={message.sid || index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                                        mb: showTime ? 1.5 : 0.5,
                                        px: 1
                                    }}
                                >
                                    <Box sx={{
                                        position: 'relative',
                                        maxWidth: '70%',
                                        minWidth: '60px'
                                    }}>
                                        {/* Message Bubble with Integrated Tail */}
                                        <Box sx={{
                                            position: 'relative',
                                            bgcolor: isOwnMessage ? '#007AFF' : '#34C759', // Apple blue vs Apple green
                                            color: '#ffffff',
                                            borderRadius: isOwnMessage ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                            px: 2,
                                            py: 1.5,
                                            wordBreak: 'break-word',
                                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                                        }}>
                                            <Typography variant="body2" sx={{
                                                fontSize: '14px',
                                                lineHeight: 1.4,
                                                fontWeight: 400
                                            }}>
                                                {message.body}
                                            </Typography>

                                            {/* Tail extending from bubble */}
                                            <Box sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                ...(isOwnMessage ? { right: -8 } : { left: -8 }),
                                                width: 0,
                                                height: 0,
                                                borderStyle: 'solid',
                                                ...(isOwnMessage ? {
                                                    borderWidth: '0 0 10px 8px',
                                                    borderColor: 'transparent transparent transparent #007AFF'
                                                } : {
                                                    borderWidth: '0 8px 10px 0',
                                                    borderColor: 'transparent #34C759 transparent transparent'
                                                })
                                            }} />
                                        </Box>

                                        {/* Timestamp */}
                                        {showTime && (
                                            <Typography variant="caption" sx={{
                                                display: 'block',
                                                textAlign: isOwnMessage ? 'right' : 'left',
                                                color: '#8e8e93',
                                                fontSize: '11px',
                                                mt: 0.5,
                                                px: 1
                                            }}>
                                                {formatMessageTime(message.dateCreated)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
                <div ref={messagesEndRef} />
            </Box>

            {/* Message Input */}
            <Box sx={{
                p: 2,
                borderTop: '1px solid #333',
                bgcolor: '#2a2a2a',
                borderRadius: '0 0 12px 12px' // Rounded bottom corners
            }}>
                {/* AI Message Button */}
                <Box sx={{ mb: 2 }}>
                    <Button
                        variant="outlined"
                        startIcon={generatingAi ? <CircularProgress size={16} /> : <BotIcon />}
                        onClick={(e) => setAiMenuAnchor(e.currentTarget)}
                        disabled={generatingAi}
                        fullWidth
                        sx={{
                            textTransform: 'none',
                            borderColor: '#666',
                            color: '#9e9e9e',
                            borderRadius: '20px', // More rounded button
                            py: 1.5, // More padding for better appearance
                            '&:hover': {
                                borderColor: '#999',
                                bgcolor: 'rgba(255, 255, 255, 0.02)' // Subtle hover effect
                            }
                        }}
                    >
                        {generatingAi ? 'Generating...' : 'AI Message Templates'}
                    </Button>

                    <Menu
                        anchorEl={aiMenuAnchor}
                        open={Boolean(aiMenuAnchor)}
                        onClose={() => setAiMenuAnchor(null)}
                        PaperProps={{
                            sx: {
                                bgcolor: '#2a2a2a',
                                border: '1px solid #333',
                                borderRadius: '12px'
                            }
                        }}
                    >
                        {Object.entries(AI_MESSAGE_TEMPLATES).map(([key, template]) => (
                            <MenuItem
                                key={key}
                                onClick={() => generateAiMessage(key)}
                                sx={{
                                    color: '#e0e0e0',
                                    borderRadius: '8px',
                                    mx: 1,
                                    my: 0.5
                                }}
                            >
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {template.title}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#9e9e9e' }}>
                                        {template.description}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        ))}
                    </Menu>
                </Box>

                {/* Message Input Field with Apple Messages styling */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Text Input Area */}
                    <Box sx={{
                        bgcolor: '#1a1a1a',
                        borderRadius: '24px', // Fully rounded borders
                        border: '1px solid #666',
                        p: 2,
                        '&:focus-within': {
                            borderColor: '#007AFF',
                            boxShadow: '0 0 0 3px rgba(0, 122, 255, 0.2)'
                        },
                        transition: 'all 0.2s ease'
                    }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={5} // Set minimum 5 rows
                            maxRows={8} // Increase max rows for better UX
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your message..."
                            variant="standard"
                            disabled={!conversation}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    sendMessage();
                                }
                            }}
                            sx={{
                                '& .MuiInput-root': {
                                    color: '#e0e0e0',
                                    fontSize: '16px',
                                    '&:before': { display: 'none' },
                                    '&:after': { display: 'none' },
                                    '&.Mui-disabled': {
                                        color: '#666',
                                        '-webkit-text-fill-color': '#666'
                                    }
                                },
                                '& .MuiInput-input': {
                                    padding: '12px 16px',
                                    borderRadius: '20px', // Rounded input area
                                    '&::placeholder': {
                                        color: '#8e8e93',
                                        opacity: 1
                                    }
                                }
                            }}
                        />
                    </Box>

                    {/* Send Button - Positioned underneath */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                            onClick={sendMessage}
                            disabled={!newMessage.trim() || !conversation || sending}
                            sx={{
                                width: 56,
                                height: 56,
                                bgcolor: newMessage.trim() && conversation && !sending ? '#007AFF' : '#666',
                                color: '#ffffff',
                                borderRadius: '28px', // Fully rounded button
                                '&:hover': {
                                    bgcolor: newMessage.trim() && conversation && !sending ? '#0056CC' : '#666',
                                    transform: 'scale(1.05)'
                                },
                                '&.Mui-disabled': {
                                    bgcolor: '#666',
                                    color: '#999'
                                },
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                            }}
                        >
                            {sending ? <CircularProgress size={24} color="inherit" /> : <SendIcon sx={{ fontSize: 24 }} />}
                        </IconButton>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default LeadConversationView;
